import { ElementRef, Injectable } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SidenavService {
    readonly sidenavMaxWidth = 600;

    private readonly _defaultCollapsedWidth = 400;

    public isSidenavExpanded = false;

    /**
     * When true (default), expand overlays content by pinning the content margin to the
     * collapsed width while the drawer grows wider on top.
     */
    public expandOverlapsContent = true;

    private _collapsedWidth = this._defaultCollapsedWidth;
    private _scrolledToBottomSubject = new BehaviorSubject<boolean>(false);
    public scrolledToBottom$ = this._scrolledToBottomSubject.asObservable();
    private _isSidenavExpandableSubject = new BehaviorSubject<boolean>(false);
    private _sidenav: MatSidenav;
    public pageName: string;

    get sidenavMinWidth(): number {
        return this._collapsedWidth;
    }

    get sidenav() {
        return this._sidenav;
    }

    get isSidenavExpandable(): boolean {
        return this._isSidenavExpandableSubject.value;
    }

    set isSidenavExpandable(value: boolean) {
        this._isSidenavExpandableSubject.next(value);
    }

    public setSidenav(sidenav: MatSidenav) {
        this._sidenav = sidenav;
        this.isSidenavExpanded = false;

        const element = this._sidenavElement();
        if (!element) {
            return;
        }

        element.style.removeProperty('width');

        requestAnimationFrame(() => {
            this._captureCollapsedWidth();
            this._updateContentMargins();
        });
    }

    setSidenavWidth(_sideNav: HTMLDivElement, width: number) {
        const clamped = Math.min(Math.max(width, this._collapsedWidth), this.sidenavMaxWidth);
        this._setSidenavWidth(clamped);
        this._syncContentLayout();
    }

    toggleExpandSidebar(): void {
        if (!this._sidenav) {
            return;
        }

        if (!this.isSidenavExpanded) {
            this._captureCollapsedWidth();
            this.isSidenavExpanded = true;
            this._setSidenavWidth(this.sidenavMaxWidth);
        } else {
            this.isSidenavExpanded = false;
            this._sidenavElement()?.style.removeProperty('width');
        }

        this._syncContentLayout();
    }

    public open() {
        return this._sidenav.open();
    }

    public close() {
        return this._sidenav.close();
    }

    public toggle(): void {
        this._sidenav.toggle();
    }

    public scrolledToBottom() {
        this._scrolledToBottomSubject.next(true);
    }

    private _syncContentLayout(): void {
        if (this.expandOverlapsContent) {
            // Keep content anchored to the collapsed width so expand overlays and
            // collapse never leaves a gap from Material measuring the expanded width.
            this._setContentMargin(this._collapsedWidth);
            requestAnimationFrame(() => this._setContentMargin(this._collapsedWidth));
            return;
        }

        this._updateContentMargins();
    }

    private _setContentMargin(width: number): void {
        const content = this._contentElement();
        if (!content) {
            return;
        }

        content.style.marginLeft = `${width}px`;
    }

    private _contentElement(): HTMLElement | null {
        const container = (this._sidenav as any)?._container;
        const contentRef = container?._content;
        const fromRef = (contentRef?._element as ElementRef)?.nativeElement ?? contentRef?.nativeElement;

        if (fromRef) {
            return fromRef;
        }

        const sidenavEl = this._sidenavElement();
        return sidenavEl?.parentElement?.querySelector('mat-sidenav-content') ?? null;
    }

    private _sidenavElement(): HTMLElement | null {
        return ((this._sidenav as any)?._elementRef as ElementRef)?.nativeElement ?? null;
    }

    private _setSidenavWidth(width: number): void {
        this._sidenavElement()?.style.setProperty('width', `${width}px`);
    }

    private _captureCollapsedWidth(): void {
        const element = this._sidenavElement();
        if (!element) {
            return;
        }

        const width = element.getBoundingClientRect().width;
        if (width > 0) {
            this._collapsedWidth = Math.round(width);
        }
    }

    private _updateContentMargins(): void {
        (this._sidenav as any)?._container?.updateContentMargins?.();
    }
}
